import { Link } from 'react-router-dom';
import { useTier } from '../context/TierContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Lock, Zap, Crown, ArrowRight } from 'lucide-react';

const TIER_INFO = {
  basic: { name: 'Basic', color: 'bg-gray-500 hover:bg-gray-600' },
  advanced: { name: 'Advanced', color: 'bg-blue-500 hover:bg-blue-600' },
  professional: { name: 'Professional', color: 'bg-purple-500 hover:bg-[#6D38A2]' }
};

export const UpgradePrompt = ({ 
  feature, 
  requiredTier: propRequiredTier,
  title, 
  description,
  compact = false,
  className = ''
}) => {
  const { getRequiredTier, currentTier, tierName } = useTier();
  const requiredTier = propRequiredTier || getRequiredTier(feature);
  const requiredTierInfo = TIER_INFO[requiredTier];

  if (compact) {
    return (
      <div className={`flex items-center gap-3 p-3 bg-muted/50 border border-dashed rounded-lg ${className}`}>
        <div className="p-2 bg-[#ffd108]/20 rounded-full">
          <Lock className="h-4 w-4 text-[#ffd108]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{title || `Upgrade to unlock`}</p>
          <p className="text-xs text-muted-foreground">
            Requires {requiredTierInfo?.name || 'upgrade'}
          </p>
        </div>
        <Link to="/settings?tab=billing">
          <Button size="sm" variant="outline" className="shrink-0">
            <Zap className="h-3 w-3 mr-1" />
            Upgrade
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <Card className={`border-dashed border-2 ${className}`}>
      <CardContent className="flex flex-col items-center justify-center py-12 px-6 text-center">
        <div className="p-4 bg-[#ffd108]/20 rounded-full mb-4">
          <Lock className="h-8 w-8 text-[#ffd108]" />
        </div>
        
        <h3 className="font-heading text-xl font-semibold mb-2">
          {title || 'Feature Locked'}
        </h3>
        
        <p className="text-muted-foreground mb-4 max-w-md">
          {description || `This feature is available on the ${requiredTierInfo?.name} plan and above.`}
        </p>

        <div className="flex items-center gap-2 mb-6">
          <Badge variant="secondary" className="text-xs">
            Your plan: {tierName}
          </Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <Badge className={`${requiredTierInfo?.color} text-white text-xs`}>
            Required: {requiredTierInfo?.name}
          </Badge>
        </div>

        <Link to="/settings?tab=billing">
          <Button className="gap-2">
            <Crown className="h-4 w-4" />
            Upgrade to {requiredTierInfo?.name}
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

export const FeatureGate = ({ 
  feature, 
  children, 
  fallback,
  title,
  description
}) => {
  const { hasFeature, loading, tierData } = useTier();

  // Show loading state while fetching tier data
  if (loading || !tierData) {
    return <div className="animate-pulse h-32 bg-muted rounded" />;
  }

  if (!hasFeature(feature)) {
    return fallback || (
      <UpgradePrompt 
        feature={feature} 
        title={title}
        description={description}
      />
    );
  }

  return children;
};

export const FeatureGateInline = ({ 
  feature, 
  children,
  lockedContent
}) => {
  const { hasFeature, loading } = useTier();

  if (loading) return null;

  if (!hasFeature(feature)) {
    return lockedContent || null;
  }

  return children;
};

export const LimitWarning = ({ resourceType, className = '' }) => {
  const { checkLimit, tierData } = useTier();
  const limit = checkLimit(resourceType);

  if (limit.allowed && limit.remaining > 2) return null;

  const isAtLimit = !limit.allowed;
  const isNearLimit = limit.allowed && limit.remaining <= 2;

  if (!isAtLimit && !isNearLimit) return null;

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${
      isAtLimit ? 'bg-destructive/10 border border-destructive/30' : 'bg-yellow-500/10 border border-yellow-500/30'
    } ${className}`}>
      <div className={`p-2 rounded-full ${isAtLimit ? 'bg-destructive/20' : 'bg-yellow-500/20'}`}>
        {isAtLimit ? (
          <Lock className={`h-4 w-4 ${isAtLimit ? 'text-destructive' : 'text-yellow-500'}`} />
        ) : (
          <Zap className="h-4 w-4 text-yellow-500" />
        )}
      </div>
      <div className="flex-1">
        <p className={`text-sm font-medium ${isAtLimit ? 'text-destructive' : 'text-yellow-600 dark:text-yellow-400'}`}>
          {isAtLimit 
            ? `${resourceType === 'operator' ? 'Mobile operator' : 'User'} limit reached (${limit.used}/${limit.limit})`
            : `Only ${limit.remaining} ${resourceType === 'operator' ? 'mobile operator' : 'user'}${limit.remaining > 1 ? 's' : ''} remaining`
          }
        </p>
        <p className="text-xs text-muted-foreground">
          {isAtLimit 
            ? 'Upgrade your plan or purchase extra slots'
            : 'Consider upgrading for more capacity'
          }
        </p>
      </div>
      <Link to="/settings?tab=billing">
        <Button size="sm" variant={isAtLimit ? 'default' : 'outline'}>
          {isAtLimit ? 'Upgrade' : 'View Plans'}
        </Button>
      </Link>
    </div>
  );
};

export default FeatureGate;
